"use client";

import React, { FC, PropsWithChildren } from "react";
import {
  GlobalStateProvider,
  ShaApplicationProvider,
  StoredFilesProvider,
  useNextRouter,
} from "@shesha-io/reactjs";
import { AppProgressBar } from "next-nprogress-bar";
import { useTheme } from "antd-style";
import { BrandPlugin } from "@/definitions/brand/BrandPlugin";
import { SetupPlugin } from "@/definitions/setup/SetupPlugin";
import { CustomComponentsPlugin } from "@/providers/customComponentsPlugin";
import { EnterpriseApplicationPlugin } from "@shesha-io/enterprise";

export interface IAppProviderProps {
  backendUrl: string;
}

/**
 * All tenant plumbing has been removed with ABP multitenancy. This previously resolved the brand to
 * a tenant id via `useTenantResolver` and then pushed `Abp.TenantId` onto every outbound request
 * through three separate channels — `buildHttpRequestHeaders`, an axios request interceptor, and a
 * `window.fetch` monkey-patch — because each one reached a different set of callers.
 *
 * None of it is needed now: PCF/PBF separation is a `Forum` column on the domain records, not a
 * request-scoped identity. `BrandPlugin` stays, since the hostname is still what supplies the brand
 * (and therefore the Forum value at registration).
 *
 * Note: a browser that used the old build may still hold `localStorage["TENANT"]` and an
 * `Abp.TenantId` cookie. Both are inert — the backend has multitenancy disabled and ignores the
 * header — so they are left to expire rather than cleared with throwaway migration code.
 */
export const AppProvider: FC<PropsWithChildren<IAppProviderProps>> = ({
  children,
  backendUrl,
}) => {
  const nextRouter = useNextRouter();
  const theme = useTheme();

  return (
    <GlobalStateProvider>
      <AppProgressBar height="4px" color={theme.colorPrimary} shallowRouting />
      <ShaApplicationProvider
        backendUrl={backendUrl}
        router={nextRouter}
        noAuth={nextRouter.path?.includes("/no-auth")}
      >
        <EnterpriseApplicationPlugin>
          <BrandPlugin />
          <SetupPlugin />
          <CustomComponentsPlugin>
            <StoredFilesProvider
              baseUrl={backendUrl}
              ownerId={""}
              ownerType={""}
            >
              {children}
            </StoredFilesProvider>
          </CustomComponentsPlugin>
        </EnterpriseApplicationPlugin>
      </ShaApplicationProvider>
    </GlobalStateProvider>
  );
};
